import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaFlagCheckered,
  FaInfoCircle,
  FaMap,
  FaMapMarkedAlt,
  FaRoad,
  FaServer,
  FaUsers,
} from 'react-icons/fa';
import Fancybox from '../components/Fancybox';
import { Helmet } from 'react-helmet-async';

const FALLBACK_BANNER = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
const CACHE_TTL = 3 * 60 * 60 * 1000;

const htmlToParagraphs = (html = '') => {
  const withBreaks = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n');

  let text = withBreaks;
  if (typeof DOMParser !== 'undefined') {
    text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent || '';
  } else {
    text = withBreaks.replace(/<[^>]*>/g, '');
  }

  return text
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const ConvoyItem = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [convoyItem, setConvoyItem] = useState(null);
  const [upcomingConvoys, setUpcomingConvoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'ru-RU';

  useEffect(() => {
    let cancelled = false;

    const readCache = (dataKey, timestampKey) => {
      const timestamp = Number(localStorage.getItem(timestampKey));
      if (!timestamp || Date.now() - timestamp >= CACHE_TTL) return null;

      try {
        return JSON.parse(localStorage.getItem(dataKey));
      } catch {
        return null;
      }
    };

    const fetchJson = async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();
    };

    const loadConvoy = async () => {
      setLoading(true);
      setError(false);
      window.scrollTo({ top: 0, behavior: 'auto' });

      const convoyCacheKey = `convoyItem-${id}`;
      const convoyTimestampKey = `convoyItemTimestamp-${id}`;
      const upcomingCacheKey = `upcomingConvoys-${id}`;
      const upcomingTimestampKey = `upcomingConvoysTimestamp-${id}`;

      try {
        const cachedConvoy = readCache(convoyCacheKey, convoyTimestampKey);
        const cachedUpcoming = readCache(upcomingCacheKey, upcomingTimestampKey);

        const [convoyResult, upcomingResult] = await Promise.allSettled([
          cachedConvoy || fetchJson(`https://hub.mileageriot.com/api/${id}/event`),
          cachedUpcoming || fetchJson(`https://hub.mileageriot.com/api/${id}/getUpcomingConvoys`),
        ]);

        if (convoyResult.status !== 'fulfilled' || !convoyResult.value?.id) {
          throw new Error('Convoy could not be loaded');
        }

        if (cancelled) return;

        setConvoyItem(convoyResult.value);
        setUpcomingConvoys(
          upcomingResult.status === 'fulfilled' && Array.isArray(upcomingResult.value)
            ? upcomingResult.value.slice(0, 4)
            : []
        );

        if (!cachedConvoy) {
          localStorage.setItem(convoyCacheKey, JSON.stringify(convoyResult.value));
          localStorage.setItem(convoyTimestampKey, Date.now().toString());
        }
        if (!cachedUpcoming && upcomingResult.status === 'fulfilled') {
          localStorage.setItem(upcomingCacheKey, JSON.stringify(upcomingResult.value));
          localStorage.setItem(upcomingTimestampKey, Date.now().toString());
        }
      } catch (loadError) {
        console.error('Error fetching convoy data:', loadError);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConvoy();
    return () => { cancelled = true; };
  }, [id]);

  const descriptionParagraphs = useMemo(
    () => htmlToParagraphs(convoyItem?.description),
    [convoyItem?.description]
  );
  const rulesParagraphs = useMemo(
    () => htmlToParagraphs(convoyItem?.rules),
    [convoyItem?.rules]
  );

  const formatDate = (dateString, long = false) => {
    if (!dateString) return '—';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: long ? 'long' : 'short',
      year: 'numeric',
    }).format(new Date(`${dateString}T12:00:00Z`));
  };

  const formatTime = (time) => (time ? time.slice(0, 5) : '—');
  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = FALLBACK_BANNER;
  };

  if (loading) {
    return (
      <section className="convoy-detail-page convoy-detail-state-page page-convoy-item">
        <div className="convoy-detail-state" role="status">
          <span className="convoy-detail-loader" aria-hidden="true"></span>
          <p>{t('events.loading')}</p>
        </div>
      </section>
    );
  }

  if (error || !convoyItem) {
    return (
      <section className="convoy-detail-page convoy-detail-state-page page-convoy-item">
        <div className="convoy-detail-state" role="alert">
          <FaCalendarAlt aria-hidden="true" />
          <h1>{t('events.detailError')}</h1>
          <p>{t('events.detailErrorHint')}</p>
          <Link to="/events" className="convoy-back-link">
            <FaArrowLeft aria-hidden="true" />
            {t('events.backToEvents')}
          </Link>
        </div>
      </section>
    );
  }

  const gameLabel = Number(convoyItem.game) === 1 ? 'ATS' : 'ETS2';
  const metaDescription = descriptionParagraphs.join(' ').slice(0, 155)
    || `${convoyItem.title} — Mileage Riot`;
  const galleryImages = [
    { src: convoyItem.route, label: t('events.routeMap'), isRoute: true },
    { src: convoyItem.start_image, label: t('events.startPoint') },
    { src: convoyItem.end_image, label: t('events.finishPoint') },
  ].filter((image) => image.src);

  return (
    <>
      <Helmet>
        <title>{`${convoyItem.title} — Mileage Riot`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://mileageriot.com/events/${id}`} />
        <meta property="og:url" content={`https://mileageriot.com/events/${id}`} />
        <meta property="og:title" content={`${convoyItem.title} — Mileage Riot`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={convoyItem.banner || FALLBACK_BANNER} />
        <meta property="og:locale" content={locale === 'en-GB' ? 'en_GB' : 'ru_RU'} />
      </Helmet>

      <main className="convoy-detail-page page-convoy-item">
        <div className="convoy-detail-grid-bg" aria-hidden="true"></div>
        <div className="convoy-detail-glow" aria-hidden="true"></div>

        <div className="container convoy-detail-container">
          <Link to="/events" className="convoy-back-link">
            <FaArrowLeft aria-hidden="true" />
            {t('events.backToEvents')}
          </Link>

          <section className="convoy-detail-hero">
            <img
              src={convoyItem.banner || FALLBACK_BANNER}
              alt=""
              className="convoy-detail-hero-image"
              onError={handleImageError}
            />
            <div className="convoy-detail-hero-shade"></div>
            <div className="convoy-detail-hero-content">
              <div className="convoy-detail-badges">
                <span><FaCalendarAlt aria-hidden="true" /> {formatDate(convoyItem.date, true)}</span>
                <span className={gameLabel === 'ATS' ? 'is-ats' : ''}>{gameLabel}</span>
                {convoyItem.host_vtc && <span>{convoyItem.host_vtc}</span>}
              </div>
              <h1>{convoyItem.title}</h1>
              <p>{t('events.convoyFromTo', {
                from: convoyItem.start_location,
                to: convoyItem.end_location,
              })}</p>
            </div>
          </section>

          <section className="convoy-schedule-strip" aria-label={t('events.scheduleSummary')}>
            <div className="convoy-schedule-item">
              <FaClock aria-hidden="true" />
              <div><span>{t('events.eventDetails.parkingTime')}</span><strong>{formatTime(convoyItem.meetup_time)} UTC</strong></div>
            </div>
            <div className="convoy-schedule-item">
              <FaArrowRight aria-hidden="true" />
              <div><span>{t('events.eventDetails.departureTime')}</span><strong>{formatTime(convoyItem.departure_time)} UTC</strong></div>
            </div>
            <div className="convoy-schedule-item">
              <FaRoad aria-hidden="true" />
              <div><span>{t('events.eventDetails.distance')}</span><strong>{convoyItem.distance || '—'} KM</strong></div>
            </div>
            <div className="convoy-schedule-item">
              <FaServer aria-hidden="true" />
              <div><span>{t('events.eventDetails.server')}</span><strong>{convoyItem.server || '—'}</strong></div>
            </div>
          </section>

          <div className="convoy-detail-layout">
            <div className="convoy-detail-main">
              {descriptionParagraphs.length > 0 && (
                <section className="convoy-content-panel">
                  <div className="convoy-content-heading">
                    <span className="convoy-content-icon"><FaInfoCircle aria-hidden="true" /></span>
                    <div><small>{t('events.aboutConvoyLabel')}</small><h2>{t('events.aboutConvoy')}</h2></div>
                  </div>
                  <div className="convoy-rich-text">
                    {descriptionParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                  </div>
                </section>
              )}

              {galleryImages.length > 0 && (
                <section className="convoy-content-panel convoy-gallery-panel">
                  <div className="convoy-content-heading">
                    <span className="convoy-content-icon"><FaMap aria-hidden="true" /></span>
                    <div><small>{t('events.routeLabel')}</small><h2>{t('events.routeMap')}</h2></div>
                  </div>
                  <Fancybox options={{ Carousel: { infinite: false } }}>
                    <div className={`convoy-gallery convoy-gallery-${galleryImages.length}`}>
                      {galleryImages.map((image) => (
                        <a data-fancybox="convoy-gallery" href={image.src} key={image.src} className={image.isRoute ? 'is-route' : ''}>
                          <img loading="lazy" decoding="async" src={image.src} alt={image.label} onError={handleImageError} />
                          <span>{image.label}</span>
                        </a>
                      ))}
                    </div>
                  </Fancybox>
                </section>
              )}

              {rulesParagraphs.length > 0 && (
                <section className="convoy-content-panel convoy-rules-panel">
                  <div className="convoy-content-heading">
                    <span className="convoy-content-icon"><FaFlagCheckered aria-hidden="true" /></span>
                    <div><small>{t('events.importantLabel')}</small><h2>{t('events.convoyDetails')}</h2></div>
                  </div>
                  <div className="convoy-rich-text convoy-rules-text">
                    {rulesParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                  </div>
                </section>
              )}
            </div>

            <aside className="convoy-route-summary">
              <div className="convoy-route-summary-card">
                <div className="convoy-route-summary-head">
                  <FaMapMarkedAlt aria-hidden="true" />
                  <div><small>{t('events.routeLabel')}</small><h2>{t('events.routeSummary')}</h2></div>
                </div>

                <div className="convoy-route-vertical">
                  <div className="convoy-route-stop">
                    <span></span>
                    <div><small>{t('events.from')}</small><strong>{convoyItem.start_location || '—'}</strong></div>
                  </div>
                  <div className="convoy-route-track"></div>
                  <div className="convoy-route-stop is-finish">
                    <span></span>
                    <div><small>{t('events.to')}</small><strong>{convoyItem.end_location || '—'}</strong></div>
                  </div>
                </div>

                <dl className="convoy-route-facts">
                  <div><dt>{t('events.eventDetails.dlc')}</dt><dd>{convoyItem.dlc || t('events.noDlc')}</dd></div>
                  <div><dt>{t('events.game')}</dt><dd>{gameLabel}</dd></div>
                  <div><dt>{t('events.organizer')}</dt><dd>{convoyItem.host_vtc || 'Mileage Riot'}</dd></div>
                </dl>

                <Link to="/events" className="convoy-route-cta">
                  {t('events.allConvoys')}
                  <FaArrowRight aria-hidden="true" />
                </Link>
              </div>
            </aside>
          </div>

          {upcomingConvoys.length > 0 && (
            <section className="convoy-upcoming-section">
              <div className="convoy-upcoming-heading">
                <div><small>{t('events.nextLabel')}</small><h2><FaUsers aria-hidden="true" /> {t('events.upcomingConvoys')}</h2></div>
                <Link to="/events">{t('events.allConvoys')} <FaArrowRight aria-hidden="true" /></Link>
              </div>
              <div className="convoy-upcoming-grid">
                {upcomingConvoys.map((convoy) => (
                  <Link to={`/events/${convoy.id}`} className="convoy-upcoming-card" key={convoy.id}>
                    <div className="convoy-upcoming-image">
                      <img loading="lazy" decoding="async" src={convoy.banner || FALLBACK_BANNER} alt="" onError={handleImageError} />
                      <span>{Number(convoy.game) === 1 ? 'ATS' : 'ETS2'}</span>
                    </div>
                    <div className="convoy-upcoming-copy">
                      <small><FaCalendarAlt aria-hidden="true" /> {formatDate(convoy.date)}</small>
                      <strong>{convoy.title}</strong>
                      <span>{t('events.viewDetails')} <FaArrowRight aria-hidden="true" /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default ConvoyItem;
